import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Truck, RefreshCw, MapPin, Gauge, Fuel, Zap, Search, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _fmtRel = (iso) => {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

/**
 * FleetVehicles — admin table listing every truck's latest ELD telemetry.
 * Highlights operationally interesting states (engine off / low fuel /
 * stale ping) so ops can spot problems at a glance.
 */
const FleetVehicles = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRows = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/eld/vehicles`, { withCredentials: true });
      setRows(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      setError(typeof msg === 'string' ? msg : 'Unable to load vehicles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
    const id = setInterval(fetchRows, 60_000);
    return () => clearInterval(id);
  }, [fetchRows]);

  const refresh = async () => {
    try { await axios.post(`${API}/eld/refresh`, {}, { withCredentials: true }); } catch { /* ignore */ }
    await fetchRows();
  };

  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      (r.label || '').toLowerCase().includes(needle)
      || (r.vin || '').toLowerCase().includes(needle)
      || (r.eld_provider || '').toLowerCase().includes(needle)
      || (r.eld_device_id || '').toLowerCase().includes(needle)
    );
  });

  const engineOffCount = rows.filter((r) => r.latest_telemetry?.engine_state === 'off').length;
  const lowFuelCount = rows.filter((r) => typeof r.latest_telemetry?.fuel_pct === 'number' && r.latest_telemetry.fuel_pct < 25).length;
  const stalePingCount = rows.filter((r) => {
    const at = r.latest_telemetry?.captured_at;
    if (!at) return true;
    return (Date.now() - new Date(at).getTime()) > 15 * 60 * 1000;
  }).length;

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="fleet-vehicles-page">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Fleet Vehicles
            </h1>
            <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Live ELD telemetry across every linked truck. Phone GPS lives separately on each driver.
            </p>
          </div>
          <button
            type="button"
            onClick={refresh}
            data-testid="fleet-vehicles-refresh"
            className="inline-flex items-center gap-2 text-sm bg-[#002FA7] text-white px-3 py-2 rounded-md font-semibold hover:bg-[#00247D]"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Trucks linked" value={rows.length} icon={Truck} tone="brand" testid="stat-linked" />
          <StatCard label="Engine off" value={engineOffCount} icon={Zap} tone={engineOffCount > 0 ? 'amber' : 'slate'} testid="stat-engine-off" />
          <StatCard label="Low fuel" value={lowFuelCount} icon={Fuel} tone={lowFuelCount > 0 ? 'red' : 'slate'} testid="stat-low-fuel" />
          <StatCard label="Stale ping (>15m)" value={stalePingCount} icon={AlertTriangle} tone={stalePingCount > 0 ? 'red' : 'slate'} testid="stat-stale" />
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
              <input
                data-testid="fleet-vehicles-search"
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by label, VIN, provider…"
                className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              />
            </div>
            <span className="text-xs text-slate-500">
              Showing {filtered.length} of {rows.length}
            </span>
          </div>

          {loading ? (
            <div className="text-sm text-slate-500 flex items-center gap-2 py-6">
              <RefreshCw className="w-4 h-4 animate-spin" /> Loading vehicles…
            </div>
          ) : error ? (
            <div data-testid="fleet-vehicles-error" className="text-xs text-red-600 py-4">
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <div
              data-testid="fleet-vehicles-empty"
              className="text-center py-10 text-xs text-slate-500 border border-dashed border-slate-200 rounded-md"
            >
              No trucks with linked ELD devices yet.
              Open a truck asset, click <span className="font-semibold">Connect Demo ELD</span> or link a real provider to start telemetry.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    <th className="py-2 px-2">Truck</th>
                    <th className="py-2 px-2">Provider</th>
                    <th className="py-2 px-2">Position</th>
                    <th className="py-2 px-2">Speed</th>
                    <th className="py-2 px-2">Odometer</th>
                    <th className="py-2 px-2">Engine</th>
                    <th className="py-2 px-2">Fuel</th>
                    <th className="py-2 px-2">Last ping</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const t = r.latest_telemetry || {};
                    const stale = t.captured_at && (Date.now() - new Date(t.captured_at).getTime()) > 15 * 60 * 1000;
                    const engineOff = t.engine_state === 'off';
                    const lowFuel = typeof t.fuel_pct === 'number' && t.fuel_pct < 25;
                    return (
                      <tr
                        key={r.truck_asset_id}
                        data-testid={`fleet-row-${r.truck_asset_id}`}
                        onClick={() => navigate(`/assets/${r.truck_asset_id}`)}
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                      >
                        <td className="py-2.5 px-2">
                          <div className="font-semibold text-slate-900">{r.label || 'Truck'}</div>
                          {r.vin && (
                            <div className="text-[10px] font-mono text-slate-400">{r.vin}</div>
                          )}
                        </td>
                        <td className="py-2.5 px-2">
                          <div className="capitalize text-slate-700">{r.eld_provider || '—'}</div>
                          <div className="text-[10px] font-mono text-slate-400">{r.eld_device_id || '—'}</div>
                        </td>
                        <td className="py-2.5 px-2 text-xs">
                          {typeof t.lat === 'number' && typeof t.lng === 'number' ? (
                            <a
                              href={`https://www.google.com/maps?q=${t.lat},${t.lng}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 font-mono text-slate-700 hover:text-[#002FA7]"
                            >
                              <MapPin className="w-3 h-3" />
                              {t.lat.toFixed(4)}, {t.lng.toFixed(4)}
                            </a>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="py-2.5 px-2">
                          {typeof t.speed_mph === 'number' ? (
                            <span className="inline-flex items-center gap-1">
                              <Gauge className="w-3 h-3 text-slate-400" />
                              {Math.round(t.speed_mph)} mph
                            </span>
                          ) : '—'}
                        </td>
                        <td className="py-2.5 px-2 text-xs">
                          {typeof t.odometer_miles === 'number'
                            ? `${Math.round(t.odometer_miles).toLocaleString()} mi`
                            : '—'}
                        </td>
                        <td className="py-2.5 px-2">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${
                            engineOff ? 'bg-amber-100 text-amber-800'
                              : t.engine_state === 'on' ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {t.engine_state || '—'}
                          </span>
                        </td>
                        <td className="py-2.5 px-2">
                          {typeof t.fuel_pct === 'number' ? (
                            <span className={lowFuel ? 'text-red-700 font-semibold' : ''}>
                              {Math.round(t.fuel_pct)}%
                            </span>
                          ) : '—'}
                        </td>
                        <td className="py-2.5 px-2 text-xs">
                          <span className={stale ? 'text-red-600 font-semibold' : 'text-slate-500'}>
                            {_fmtRel(t.captured_at)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

const StatCard = ({ label, value, icon: Icon, tone, testid }) => {
  const tones = {
    brand: 'border-[#002FA7]/30 bg-[#002FA7]/5 text-[#002FA7]',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    red: 'border-red-200 bg-red-50 text-red-800',
    slate: 'border-slate-200 bg-white text-slate-700',
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-md border p-4 ${tones[tone] || tones.slate}`}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">{label}</div>
      </div>
      <div className="text-2xl font-bold mt-1 text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{value}</div>
    </div>
  );
};

export default FleetVehicles;
